// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * #106 (ADR-045): durable Amy conversation identity — web twin of
 * mobile/src/lib/amyConversation.ts (same decision, keep in sync).
 *
 * The SERVER owns the conversationId (GET /v1/amy/conversation): the same
 * BiteBalance conversation keeps the same id across tab close, browser
 * restart, container rotation, behavior deploys, tabs, and devices. Local
 * storage here is ONLY a cache for speed:
 *
 * - warm cache → return immediately, refresh from the server in the
 *   background (a server-side change, e.g. a new chat started on another
 *   device, is adopted on the NEXT open — never mid-conversation);
 * - cold cache → fetch with retries; on total failure the send FAILS
 *   CLOSED (ConversationIdentityUnavailable): silently minting a local id
 *   would create a permanent orphan memory thread, which is worse than a
 *   rare, honest "try again" (#106 item 12 decision). If the identity
 *   endpoint is down, the whole API is down anyway.
 * - logout clears the cache (shared-device hygiene; the authoritative
 *   record is server-side and per-user, so nothing is lost).
 */

export interface AmyConversationIdentity {
  conversationId: string
  /** How this open obtained the id — AMY_TURN telemetry passthrough. */
  source: "cache" | "registered" | "adopted" | "minted"
}

export interface AmyConversationCache {
  get(key: string): string | null
  set(key: string, value: string): void
  remove(key: string): void
}

const CONVERSATION_KEY_PREFIX = "bb_amy_conv_"
const FETCH_RETRIES = 2
const RETRY_DELAY_MS = 600

export class ConversationIdentityUnavailable extends Error {
  constructor() {
    super("conversation identity unavailable")
    this.name = "ConversationIdentityUnavailable"
  }
}

export function browserConversationCache(): AmyConversationCache {
  return {
    get: (key) => {
      try {
        return window.localStorage.getItem(key)
      } catch {
        return null
      }
    },
    set: (key, value) => {
      try {
        window.localStorage.setItem(key, value)
      } catch {
        /* cache only — server stays authoritative */
      }
    },
    remove: (key) => {
      try {
        window.localStorage.removeItem(key)
      } catch {
        /* best-effort */
      }
    },
  }
}

export function conversationCacheKey(userKey: string): string {
  return `${CONVERSATION_KEY_PREFIX}${userKey}`
}

export async function durableConversationId(opts: {
  fetchIdentity: () => Promise<{ conversationId: string; source: string }>
  cache: AmyConversationCache
  userKey: string
  /** Injectable for tests. */
  delay?: (ms: number) => Promise<void>
}): Promise<AmyConversationIdentity> {
  const key = conversationCacheKey(opts.userKey)
  const cached = opts.cache.get(key)
  if (cached && cached.length > 0) {
    // Background refresh keeps the cache honest without blocking the send.
    void opts
      .fetchIdentity()
      .then((identity) => {
        if (identity.conversationId && identity.conversationId !== cached) {
          opts.cache.set(key, identity.conversationId)
        }
      })
      .catch(() => {
        /* cache remains valid; server unreachable is handled on cold opens */
      })
    return { conversationId: cached, source: "cache" }
  }

  const delay = opts.delay ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)))
  let lastError: unknown
  for (let attempt = 0; attempt <= FETCH_RETRIES; attempt++) {
    try {
      const identity = await opts.fetchIdentity()
      if (typeof identity.conversationId === "string" && identity.conversationId.length > 0) {
        opts.cache.set(key, identity.conversationId)
        const source =
          identity.source === "adopted" || identity.source === "minted" ? identity.source : "registered"
        return { conversationId: identity.conversationId, source }
      }
      lastError = new Error("empty identity response")
    } catch (err) {
      lastError = err
    }
    if (attempt < FETCH_RETRIES) await delay(RETRY_DELAY_MS * (attempt + 1))
  }
  // eslint-disable-next-line no-console
  console.error("AMY_CONVERSATION_IDENTITY_UNAVAILABLE", lastError)
  throw new ConversationIdentityUnavailable()
}

/** Logout hygiene: forget the cached conversation id for this user. */
export function clearConversationCache(cache: AmyConversationCache, userKey: string): void {
  cache.remove(conversationCacheKey(userKey))
}
